import React, { useEffect, useState, useCallback, useRef } from 'react';
import pb from '../lib/pb';
import { getPreview, setPreview } from '../lib/spriteCache';
import { loadIndex, saveIndex } from '../lib/spriteIndexCache';
import { listLocalSprites, /* NYTT: */ removeLocalSprite } from '../lib/localSprites';
import { SpriteRecord } from '../types/sprites';
import { useThumbFallback } from '../hooks/useThumbFallback';
import LibraryHeader from './LibraryHeader';
import SpriteCard from './SpriteCard';
import { PICO8_PALETTE} from '../lib/pico8';
import { pixelsToDataUrl } from '../lib/pixelsToDataUrl';
import { fileThumbUrl } from '../lib/fileThumbUrl';
import { mergeManySpritesIntoP8Text } from '../lib/pico8Sheet';
import { PICO8_SECONDARY } from '../utils/palette';
import { spritesToPngSheet } from '../lib/exportSpriteSheet';

interface LibraryTabProps {
  onOpenSprite: (p: number[][], palette: string[]) => void;
  onRequireAuth: () => void;
  refreshSignal?: number; // ändras för att trigga reload
}

const LibraryTab: React.FC<LibraryTabProps> = ({ onOpenSprite, onRequireAuth, refreshSignal }) => {
  const [sprites, setSprites] = useState<SpriteRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [, setError] = useState<string | null>(null); // eslint-ignore: only setter used

  // NYTT: exportläge och val
  const [exportMode, setExportMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [merging, setMerging] = useState(false);
  const [pngExporting] = useState(false);
  // File input för BULK MERGE (måste vara hook på top-level)
  const mergeFileRef = useRef<HTMLInputElement | null>(null);
  const onMergeFileChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    e.currentTarget.value = '';
    if (!file) return;
    await bulkMergeSelected(file);
  };

  // NYTT: delete‑status
  const [, setDeletingId] = useState<string | null>(null); // eslint-ignore: only setter used

  // ADD: onImgError factory
  const onImgError = useThumbFallback(setSprites);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (!pb.authStore.isValid) {
        // Lokal “library” när man inte är inloggad
        const records = listLocalSprites();
        const mapped = records.map((rec: any) => {
          let previewUrl: string | undefined;
          if (rec.pixels) {
            previewUrl = pixelsToDataUrl(rec.pixels, rec.palette || PICO8_PALETTE, 128, PICO8_SECONDARY);
          }
          const displayName = (String(rec.name ?? rec.title ?? rec.label ?? '').trim() || 'untitled');
          return { ...rec, previewUrl, displayName };
        });
        setSprites(mapped);
        return; // outer finally kommer fortfarande köras
      }

    // 0) Visa ev. cache direkt (snabb upplevelse)
    const userId = (pb.authStore.record as any)?.id as string | undefined;
    const cached = loadIndex(userId);
    if (cached?.records?.length) {
      const mappedFromCache = cached.records.map((rec: any) => {
        const fileName = rec.preview || rec.image || rec.thumb || rec.file;
        const updated = rec.updated ?? rec.updatedAt ?? rec.updated_at ?? rec['@updated'] ?? 0;

        let previewUrl = getPreview(rec.id, updated);
        let pixelFallback = '';

        if (!previewUrl && fileName) {
          previewUrl = fileThumbUrl(rec, fileName);
        }
        if (rec.pixels) {
          pixelFallback = pixelsToDataUrl(rec.pixels, rec.palette || PICO8_PALETTE, 128, PICO8_SECONDARY);
          if (!previewUrl && pixelFallback) previewUrl = pixelFallback;
        }
        if (previewUrl) { try { setPreview(rec.id, updated, previewUrl); } catch {} }

        const displayName = (String(rec.name ?? rec.title ?? rec.label ?? '').trim() || 'untitled');
        return { ...rec, previewUrl, __pixelFallback: pixelFallback, displayName, updated }; // <— lägg till fallback + updated
      });
      mappedFromCache.sort((a,b)=> (a.displayName||'').localeCompare(b.displayName||'', 'sv', { sensitivity: 'base' }));
      setSprites(mappedFromCache);
    }

    // 1) Billig freshness‑check (1 item + totalItems)
    setLoading(!cached); // visa loader bara om vi inte hade cache att visa
    try {
      const peek = await pb.collection('sprites').getList(1, 1, {
        sort: '-updated',
        fields: 'id,@collectionName,@collectionId,updated,updatedAt,updated_at,@updated,name,title,label,preview,image,thumb,file'
      } as any);
      const latest = (() => {
        const rec = peek.items?.[0];
        return rec ? (rec.updated ?? rec.updatedAt ?? rec.updated_at ?? (rec as any)['@updated'] ?? 0) : null;
      })();
      const total = (peek as any).totalItems ?? peek.items?.length ?? 0;

      // Om cache finns och både senaste updated och antal matchar → hoppa över full fetch
      if (cached && cached.latestUpdated === latest && cached.total === total) {
        setLoading(false);
        return;
      }

      // 2) Full fetch (oförändrat fälturval för snabbare svar)
      const records: any[] = await pb.collection('sprites').getFullList({
        fields: 'id,@collectionName,@collectionId,name,title,label,updated,updatedAt,updated_at,@updated,preview,image,thumb,file,pixels,palette'
      } as any);

      const mapped = records.map((rec: any) => {
        const fileName = rec.preview || rec.image || rec.thumb || rec.file;
        const updated = rec.updated ?? rec.updatedAt ?? rec.updated_at ?? rec['@updated'] ?? 0;

        // 1) try cache
        let previewUrl = getPreview(rec.id, updated);

        // 2) compute URL + pixel fallback
        let pixelFallback = '';
        if (!previewUrl && fileName) previewUrl = fileThumbUrl(rec, fileName);
        if (rec.pixels) {
          pixelFallback = pixelsToDataUrl(rec.pixels, rec.palette || PICO8_PALETTE, 128, PICO8_SECONDARY);
          // if no file, use pixels directly
          if (!previewUrl && pixelFallback) previewUrl = pixelFallback;
        }
        if (previewUrl) { try { setPreview(rec.id, updated, previewUrl); } catch {} }

        const displayName = (String(rec.name ?? rec.title ?? rec.label ?? '').trim() || 'untitled');
        return { ...rec, previewUrl, __pixelFallback: pixelFallback, displayName, updated };
      });

      mapped.sort((a,b)=> (a.displayName||'').localeCompare(b.displayName||'', 'sv', { sensitivity: 'base' }));
      setSprites(mapped);

      // 3) Spara ny index‑cache
      const latestFromFull = mapped.length ? (records[0].updated ?? records[0].updatedAt ?? records[0].updated_at ?? records[0]['@updated'] ?? latest) : latest;
      saveIndex(userId, { latestUpdated: latestFromFull, total: mapped.length, records });
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      //setLoading(false);
    }
  } catch (e: any) {
    // fångar ev. fel ovanför inner try
    setError(e?.message || 'Failed to load');
  } finally {
    // säkerställ att loading alltid släcks, även vid early return
    setLoading(false);
  }
}, []);
  useEffect(() => { load(); }, [load]);

  // Ladda om när refreshSignal ändras (t.ex. efter save i editorn)
  // ÄNDRAT: ta bort auth‑villkoret – vi vill även ladda lokala sprites
  useEffect(() => {
    load();
  }, [load, refreshSignal]);

  // Auto-refresh när auth-status ändras
  useEffect(() => {
    const unsub = pb.authStore.onChange(() => {
      load(); // ÄNDRAT: ladda om oavsett in/utloggad
    });
    return unsub;
  }, [load]);

  const handleLogout = () => {
    try { pb.authStore.clear(); setSprites([]); }
    catch (e) { console.error('logout failed', e); }
  };




  // Starta Google-login via egen frontend-callback
  const loginWithGoogle = async () => {
    const callbackUrl = `${window.location.origin}/oauth2-callback`;
    const base = pb.baseUrl.replace(/\/+$/, '');
    const url = `${base}/api/collections/users/auth-methods?redirectUrl=${encodeURIComponent(callbackUrl)}`;

    console.groupCollapsed('[OAuth][Google] redirect flow');
    console.info('[OAuth] PB base:', pb.baseUrl);
    console.info('[OAuth] callbackUrl:', callbackUrl);
    try {
      const res = await fetch(url, { method: 'GET' });
      const body = await res.json();
      const providers: any[] = body?.authProviders ?? body?.oauth2?.providers ?? [];
      const google = providers.find((p: any) => p?.name === 'google');
      if (!google) {
        console.error('[OAuth] Google provider saknas i /auth-methods.');
        alert('Google provider saknas i PocketBase.');
        return;
      }

      // Spara verifier & state tills callbacken kör
      sessionStorage.setItem('pb_oauth_state', google.state);
      sessionStorage.setItem('pb_oauth_verifier', google.codeVerifier);

      // Sätt/ersätt redirect_uri => vår callback
      let authUrl: string = google.authUrl || '';
      try {
        const u = new URL(authUrl);
        u.searchParams.set('redirect_uri', callbackUrl);
        authUrl = u.toString();
      } catch {
        authUrl += (authUrl.includes('?') ? '&' : '?') + 'redirect_uri=' + encodeURIComponent(callbackUrl);
      }
      console.debug('[OAuth] authUrl:', authUrl.slice(0, 200));

      // Full page-redirect (minimerar popup-problem)
      window.location.assign(authUrl);
    } catch (e) {
      console.error('[OAuth] start error:', e);
      alert('Kunde inte starta Google-login.');
    } finally {
      console.groupEnd();
    }
  };

  // NYTT: radera en sprite (med snabb confirm)
  const handleDelete = async (id: string) => {
    if (exportMode) return;
    const ok = window.confirm('Delete this sprite permanently?');
    if (!ok) return;

    // NYTT: hantera lokal radering när man är utloggad
    if (!pb.authStore.isValid) {
      setDeletingId(id);
      try {
        removeLocalSprite(id);
        setSprites(list => list.filter(s => s.id !== id));
        setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
      } finally {
        setDeletingId(null);
      }
      return;
    }

    setDeletingId(id);
    try {
      await pb.collection('sprites').delete(id);
      setSprites(list => list.filter(s => s.id !== id));
      setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
    } catch (e) {
      console.error('delete failed', e);
      alert('Failed to delete sprite.');
    } finally {
      setDeletingId(null);
    }
  };

  // REPLACE the function declaration with a memoized version
  const fetchSpriteData = useCallback(async (id: string): Promise<{pixels:number[][]; palette:string[]}> => {
    const local = sprites.find(s=>s.id===id);
    if (local?.pixels && local?.palette) return { pixels: local.pixels, palette: local.palette };
    const rec = await pb.collection('sprites').getOne(id);
    return { pixels: rec.pixels, palette: rec.palette ?? PICO8_PALETTE };
  }, [sprites]);

  // REPLACE exportSelectedPNG to use the memoized fetcher and correct deps
  const exportSelectedPNG = useCallback(async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;

    const data = await Promise.all(ids.map(fetchSpriteData));

    const { dataUrl } = await spritesToPngSheet(
      data.map(d => ({ pixels: d.pixels, palette: d.palette })),
      {
        scale: 1,
        background: 'transparent',
        primaryColors: PICO8_PALETTE,
        secondaryColors: PICO8_SECONDARY,
      }
    );

    const a = document.createElement('a');
    a.download = 'sprites_sheet.png';
    a.href = dataUrl;
    a.click();
  }, [selected, fetchSpriteData]);

  const bulkMergeSelected = async (file: File) => {
    if (selected.size === 0) return;
    setMerging(true);
    try {
      const arr = Array.from(selected);
      const data: { pixels: number[][]; palette: string[] }[] = [];

      for (const id of arr) {
        try {
          const s = await fetchSpriteData(id);
          // Använd full storlek (8 eller 16) – ingen beskärning här
          const px = (s.pixels || []).map(row => row.slice());
          data.push({ pixels: px, palette: s.palette || PICO8_PALETTE });
        } catch (e) {
          console.warn('skip sprite', id, e);
        }
      }

      if (data.length === 0) { setMerging(false); return; }

      const original = await file.text();
      const { text: mergedText, mergedCount } = mergeManySpritesIntoP8Text(original, data);
      const blob = new Blob([mergedText], { type: 'text/plain;charset=utf-8' });
      const link = document.createElement('a');
      link.download = file.name.replace(/\.p8$/i, '') + `-merged_${mergedCount}.p8`;
      link.href = URL.createObjectURL(blob);
      link.click();
      URL.revokeObjectURL(link.href);

      setExportMode(false);
      setSelected(new Set());
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : 'Failed to bulk merge into .p8');
    } finally {
      setMerging(false);
    }
  };

  const toggleSelected = useCallback((id: string) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }, []);

  const isLoggedIn = pb.authStore.isValid;
  const hasLocal = !isLoggedIn && sprites.length > 0;
  const canExport = isLoggedIn || hasLocal;

  // turn off export mode if canExport becomes false (e.g. logged out and no locals)
  useEffect(() => {
    if (!canExport && exportMode) setExportMode(false);
  }, [canExport, exportMode]);

  return (
    <div className="library-tab">
      {/* hidden input för BULK MERGE */}
      <input
        ref={mergeFileRef}
        type="file"
        accept=".p8"
        onChange={onMergeFileChange}
        style={{ display:'none' }}
      />

      {/* Show local banner only if there are local sprites */}
      {!isLoggedIn && hasLocal && (
        <div className="local-banner" style={{ width:'100%', margin:'8px 0 12px', padding:'0 4px' }}>
          <div style={{ fontSize: 18, letterSpacing: 1, fontWeight: 600 }}>
            Local library <span style={{ opacity:.8, fontWeight: 400 }}>(this device only)</span>
          </div>
        </div>
      )}

      <LibraryHeader
        canExport={canExport}
        exportMode={exportMode}
        selectedCount={selected.size}
        merging={merging}
        pngExporting={pngExporting}
        onEnterExport={()=>{ setExportMode(true); setSelected(new Set()); }}
        onMergeClick={()=> mergeFileRef.current?.click()}
        onExportPng={exportSelectedPNG}
        onCancel={()=>{ setExportMode(false); setSelected(new Set()); }}
      />

      {/* Empty state when logged out and no local sprites */}
      {!isLoggedIn && !hasLocal && !loading && (
        <div className="empty-state" style={{ marginTop: 12 }}>
          <h3 style={{ margin: '0 0 8px' }}>Your sprites</h3>
          <p style={{ margin: '0 0 16px', opacity: 0.85 }}>Please log in to see your saved sprites.</p>
          <div className="auth-actions" style={{ display:'flex', gap:12 }}>
            <button className="tool-button" onClick={loginWithGoogle} title="Sign in with Google">
              SIGN IN WITH GOOGLE
            </button>
            <button className="tool-button" onClick={onRequireAuth}>
              LOGIN / SIGNUP
            </button>
          </div>
        </div>
      )}

      {/* Skeleton while first loading */}
      {loading && sprites.length === 0 && (
        <div className="sprite-grid skeleton-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(128px, 1fr))', gap:16, alignItems:'start' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div className="library-item skeleton-card" key={i}>
              <div className="skeleton-thumb" />
              <div className="skeleton-title" />
            </div>
          ))}
        </div>
      )}

      {/* Grid shown when logged in, or logged out with local sprites */}
      {!loading && (isLoggedIn || hasLocal) && (
        <div className="sprite-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(128px, 1fr))', gap:16, alignItems:'start' }}>
          {sprites.map(sp => {
            const name = sp.displayName || sp.name || 'untitled';
            const checked = selected.has(sp.id);
            return (
              <SpriteCard
                key={sp.id}
                sp={sp}
                name={name}
                exportMode={exportMode}
                checked={checked}
                onToggleSelected={toggleSelected}
                onImgError={onImgError}
                onOpen={async () => {
                  if (exportMode) { toggleSelected(sp.id); return; }
                  if (sp.pixels && sp.palette) {
                    onOpenSprite(sp.pixels, sp.palette);
                  } else {
                    const full = await fetchSpriteData(sp.id);
                    onOpenSprite(full.pixels, full.palette || PICO8_PALETTE);
                  }
                }}
                onDelete={!exportMode ? (() => handleDelete(sp.id)) : undefined}
              />
            );
          })}
        </div>
      )}

      {/* NYTT: auth-knappar UNDER griden i utloggat läge */}
      {!isLoggedIn && hasLocal && (
        <div className="auth-actions" style={{ marginTop: 24, display:'flex', gap:12 }}>
          <button className="tool-button" onClick={loginWithGoogle} title="Sign in with Google">
            SIGN IN WITH GOOGLE
          </button>
          <button className="tool-button" onClick={onRequireAuth}>
            LOGIN / SIGNUP
          </button>
        </div>
      )}

      {/* Footer med logga ut när inloggad */}
      {isLoggedIn && (
        <div className="library-footer" style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <button className="tool-button" onClick={handleLogout} title="Log out">
            LOG OUT
          </button>
        </div>
      )}
    </div>
  );
}

// Lägg till default‑export så importen i App.tsx fungerar
export default LibraryTab;